import React from "react";

import { getPreviewT } from "./previewI18n";
import { ScanToContext } from "../index";
import { DEFAULT_ACTIONS, DEFAULT_OFFER_ICON_PATH } from "./types";
import type { ScanToMobilePreviewProps } from "./types";
import {
    MobilePreview,
    PreviewHeader,
    HeaderLogo,
    HeaderRight,
    LanguageSelector,
    TableBadge,
    PreviewContent,
    SectionAnchor,
} from "./styled";
import {
    PREVIEW_MODE_WITH_ORDER,
    isRtlLanguage,
    BANNER_TAB_INDEX,
    OFFERS_TAB_INDEX,
    ACTIONS_TAB_INDEX,
    MY_ORDER_TAB_INDEX,
    ADVERTISING_TAB_INDEX,
    MISCELLANEOUS_TAB_INDEX,
} from "../shared/constants";
import { BannerSection } from "./BannerSection";
import { OffersSection } from "./OffersSection";
import { ActionsSection } from "./ActionsSection";
import { AdvertisingSection } from "./AdvertisingSection";
import { OrderSection } from "./OrderSection";
import { FooterSection } from "./FooterSection";
import { PaymentPopup } from "./PaymentPopup";
import { ServerCallPopup } from "./ServerCallPopup";

// Re-export all types, constants and icons so existing imports work
export {
    ACTION_ICONS,
    DEFAULT_ACTIONS,
    SOCIAL_ICONS,
    IMAGE_PLACEHOLDER_PATH,
    DEFAULT_OFFER_ICON_PATH,
} from "./types";
export type {
    BannerPreviewData,
    OffresPreviewData,
    ActionsPreviewData,
    PublicitesPreviewData,
    CommandePreviewData,
    SocialChannel,
    DiversPreviewData,
    PaiementPreviewData,
    AppelServeurPreviewData,
    ScanToMobilePreviewProps,
} from "./types";

// ─── Component ───────────────────────────────────────────

export function ScanToMobilePreview({
    mainColor,
    selectedLanguage,
    bannerData,
    offresData,
    actionsData,
    publicitesData,
    commandeData,
    diversData,
    paiementData,
    showPaymentPopup = false,
    appelServeurData,
    showServerCallPopup = false,
    activeTab,
}: ScanToMobilePreviewProps): JSX.Element {
    // The preview chrome renders in the PREVIEW language (not the dashboard UI
    // language), using a self-contained dictionary — see ./previewI18n.
    const t = React.useMemo(() => getPreviewT(selectedLanguage), [
        selectedLanguage,
    ]);
    const { appName } = React.useContext(ScanToContext);

    // ─── Tab-driven scrolling ────────────────────────────
    // The scroll container (PreviewContent) plus one anchor per scrollable
    // section. Opening a tab scrolls its section to the top. Anchors are
    // zero-box (display:contents) so they never add layout gaps even when a
    // section renders nothing (e.g. its toggle is off).
    const previewContentRef = React.useRef<HTMLDivElement>(null);
    const bannerAnchorRef = React.useRef<HTMLDivElement>(null);
    const offersAnchorRef = React.useRef<HTMLDivElement>(null);
    const actionsAnchorRef = React.useRef<HTMLDivElement>(null);
    const orderAnchorRef = React.useRef<HTMLDivElement>(null);
    const adsAnchorRef = React.useRef<HTMLDivElement>(null);
    const footerAnchorRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const anchorByTab: Record<
            number,
            React.RefObject<HTMLDivElement>
        > = {
            [BANNER_TAB_INDEX]: bannerAnchorRef,
            [OFFERS_TAB_INDEX]: offersAnchorRef,
            [ACTIONS_TAB_INDEX]: actionsAnchorRef,
            [MY_ORDER_TAB_INDEX]: orderAnchorRef,
            [ADVERTISING_TAB_INDEX]: adsAnchorRef,
            [MISCELLANEOUS_TAB_INDEX]: footerAnchorRef,
        };
        const anchor = activeTab != null ? anchorByTab[activeTab] : undefined;
        const container = previewContentRef.current;
        // The anchor div is display:contents, so its own box is empty — the
        // section's real element is its first child. Missing child = section
        // hidden (toggle off) → leave the scroll position untouched.
        const target = anchor?.current?.firstElementChild as
            | HTMLElement
            | null
            | undefined;
        if (!container || !target) return;

        // Defer to next frame so layout reflects the freshly shown tab/section.
        const raf = requestAnimationFrame(() => {
            const top =
                target.getBoundingClientRect().top -
                container.getBoundingClientRect().top +
                container.scrollTop;
            container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        });
        return () => cancelAnimationFrame(raf);
    }, [activeTab]);

    // ─── Resolved data ───────────────────────────────────
    const offersEnabled = offresData
        ? offresData.offersEnabled !== false
        : true;
    const offersTitleVisible = offresData
        ? offresData.titleVisible !== false
        : true;
    // `|| ""` keeps the value a definite string: react-i18next's t() is typed
    // string | undefined under newer TS, which the section props (string) reject.
    const offersSectionTitle =
        offresData?.sectionTitle || t("Offers of the day") || "";
    const offersIconPath =
        offresData?.sectionIconPath || DEFAULT_OFFER_ICON_PATH;
    const offersCustomIcon = offresData?.customIconUrl || "";
    const offers = offresData?.offers || [];
    // Has the user touched the Offres tab in this modal session? Lets
    // OffersSection distinguish "tab never visited → show demo
    // fallback" from "user explicitly deactivated every offer → show
    // nothing".
    const offersConfigured = offresData != null;

    const actionsEnabled = actionsData
        ? actionsData.actionsEnabled !== false
        : true;
    const actionsTitleVisible = actionsData
        ? actionsData.sectionTitleVisible !== false
        : true;
    const actionsSectionTitle =
        actionsData?.sectionTitle || t("What would you like to do?") || "";
    const actionsIconPath =
        actionsData?.sectionIconPath || "M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z";
    const actionsCustomIcon = actionsData?.customIconUrl || "";
    const actionsList = actionsData?.actions || DEFAULT_ACTIONS;

    const adsEnabled = publicitesData
        ? publicitesData.adsEnabled !== false
        : true;
    const adsTitleVisible = publicitesData
        ? publicitesData.sectionTitleVisible !== false
        : true;
    const adsSectionTitle =
        publicitesData?.sectionTitle || t("ADVERTISING") || "";
    const adsIconPath =
        publicitesData?.sectionIconPath ||
        "M18 16v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-5 0h-2v-2h2v2zm0-4h-2V8h2v4zm-1 10c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2z";
    const adsCustomIcon = publicitesData?.customIconUrl || "";
    const adsList = publicitesData?.ads || [];
    // Discriminator: tab visited at least once vs never touched. Lets
    // AdvertisingSection distinguish "user deactivated every ad" (hide
    // section entirely) from "tab never opened" (render demo fallback).
    // Mirrors offersConfigured for Offres.
    const adsConfigured = publicitesData != null;

    // Order section
    const orderEnabled = commandeData
        ? commandeData.orderEnabled !== false
        : true;
    const orderTitleVisible = commandeData
        ? commandeData.sectionTitleVisible !== false
        : true;
    const orderSectionTitle =
        commandeData?.sectionTitle || t("My order") || "";
    const orderIconPath =
        commandeData?.sectionIconPath ||
        "M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z";
    const orderCustomIcon = commandeData?.customIconUrl || "";
    const showStepper = commandeData
        ? commandeData.showStepper !== false
        : true;
    const showOrderDetails = commandeData
        ? commandeData.showOrderDetails !== false
        : true;
    const previewMode = commandeData?.previewMode || PREVIEW_MODE_WITH_ORDER;

    const defaultStatuses = [
        { key: "ordered", label: t("Ordered"), active: true },
        { key: "preparing", label: t("In preparation"), active: true },
        { key: "ready", label: t("Ready"), active: false },
        { key: "served", label: t("Served"), active: false },
    ];
    const orderStatuses = commandeData?.orderStatuses || defaultStatuses;
    // Find the index of the last active step for the stepper
    const activeStepIndex = orderStatuses.reduce(
        (lastIdx, s, idx) => (s.active ? idx : lastIdx),
        0
    );

    const primaryColor = diversData?.primaryColor || mainColor;
    const secondaryColor = diversData?.secondaryColor || mainColor;
    // Arabic (and any future RTL locale) flips the whole preview right-to-left.
    const isRtl = isRtlLanguage(selectedLanguage);

    return (
        <MobilePreview dir={isRtl ? "rtl" : "ltr"}>
            {/* Header */}
            <PreviewHeader mainColor={mainColor} primaryColor={primaryColor}>
                <HeaderLogo>
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill={secondaryColor}
                    >
                        <path d="M3 11h8V3H3v8zm2-6h4v4H5V5zm8-2v8h8V3h-8zm6 6h-4V5h4v4zM3 21h8v-8H3v8zm2-6h4v4H5v-4zm11 3h2v-2h-2v2zm-3 3h2v-2h-2v2zm2-4h2v2h-2v-2zm2 2h2v2h-2v-2zm0-4h2v2h-2v-2zm4 4h2v-2h-2v2zm-6-6h2v2h-2v2zm4 0h2v2h-2v2z" />
                    </svg>
                    <span style={{ color: secondaryColor }}>
                        SCAN TO {appName.toUpperCase()}
                    </span>
                </HeaderLogo>
                <HeaderRight>
                    <LanguageSelector>
                        {(
                            selectedLanguage || diversData?.defaultLanguage ||"fr"
                        ).toUpperCase()}
                        <svg
                            width="8"
                            height="8"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                        >
                            <path d="M7 10l5 5 5-5z" />
                        </svg>
                    </LanguageSelector>
                    <TableBadge>Table 12</TableBadge>
                </HeaderRight>
            </PreviewHeader>
            {/* Content */}
            <PreviewContent ref={previewContentRef}>
                {/* Banner */}
                <SectionAnchor ref={bannerAnchorRef}>
                    <BannerSection
                        t={t}
                        bannerData={bannerData}
                        mainColor={mainColor}
                        selectedLanguage={selectedLanguage}
                        primaryColor={primaryColor}
                        secondaryColor={secondaryColor}
                    />
                </SectionAnchor>

                {/* Offers Section */}
                <SectionAnchor ref={offersAnchorRef}>
                    <OffersSection
                        t={t}
                        offersEnabled={offersEnabled}
                        offersTitleVisible={offersTitleVisible}
                        offersConfigured={offersConfigured}
                        offersSectionTitle={offersSectionTitle}
                        offersIconPath={offersIconPath}
                        offersCustomIcon={offersCustomIcon}
                        offers={offers}
                        mainColor={mainColor}
                        primaryColor={primaryColor}
                        secondaryColor={secondaryColor}
                    />
                </SectionAnchor>

                {/* Actions Section */}
                <SectionAnchor ref={actionsAnchorRef}>
                    <ActionsSection
                        actionsEnabled={actionsEnabled}
                        actionsTitleVisible={actionsTitleVisible}
                        actionsSectionTitle={actionsSectionTitle}
                        actionsIconPath={actionsIconPath}
                        actionsCustomIcon={actionsCustomIcon}
                        actionsList={actionsList}
                        mainColor={mainColor}
                        primaryColor={primaryColor}
                    />
                </SectionAnchor>

                {/* Advertising Section */}
                <SectionAnchor ref={adsAnchorRef}>
                    <AdvertisingSection
                        t={t}
                        adsEnabled={adsEnabled}
                        adsTitleVisible={adsTitleVisible}
                        adsConfigured={adsConfigured}
                        adsSectionTitle={adsSectionTitle}
                        adsIconPath={adsIconPath}
                        adsCustomIcon={adsCustomIcon}
                        adsList={adsList}
                        mainColor={mainColor}
                        primaryColor={primaryColor}
                    />
                </SectionAnchor>

                {/* Order Section */}
                <SectionAnchor ref={orderAnchorRef}>
                    <OrderSection
                        t={t}
                        orderEnabled={orderEnabled}
                        orderTitleVisible={orderTitleVisible}
                        orderSectionTitle={orderSectionTitle}
                        orderIconPath={orderIconPath}
                        orderCustomIcon={orderCustomIcon}
                        previewMode={previewMode}
                        showStepper={showStepper}
                        showOrderDetails={showOrderDetails}
                        orderStatuses={orderStatuses}
                        activeStepIndex={activeStepIndex}
                        mainColor={mainColor}
                        primaryColor={primaryColor}
                    />
                </SectionAnchor>

                {/* Rich Footer */}
                <SectionAnchor ref={footerAnchorRef}>
                    <FooterSection
                        t={t}
                        diversData={diversData}
                        primaryColor={primaryColor}
                    />
                </SectionAnchor>
            </PreviewContent>

            {/* Payment Popup Overlay */}
            <PaymentPopup
                t={t}
                showPaymentPopup={showPaymentPopup}
                paiementData={paiementData}
                primaryColor={primaryColor}
            />

            {/* Server Call Popup Overlay */}
            <ServerCallPopup
                t={t}
                showServerCallPopup={showServerCallPopup}
                appelServeurData={appelServeurData}
                primaryColor={primaryColor}
            />
        </MobilePreview>
    );
}
